import { Injectable } from '@angular/core';
import { APP_ROUTES, ONBOARDING_ROUTES } from 'src/app/shared/enums/appRoutes.enum';

export const PATIENT_ID_PARAM = 'pid';
export const PARTNER_REF_PARAM = 'ref';
export const PATIENT_HANDOFF_TTL_MS = 15 * 60 * 1000;
export const PATIENT_ID_MAX_LENGTH = 64;

const PATIENT_ID_KEY = 'handoffPatientId';
const REFERENCE_KEY = 'handoffReference';
const CAPTURED_AT_KEY = 'handoffCapturedAt';
const PATIENT_ID_PATTERN = new RegExp(`^[A-Za-z0-9._:/-]{1,${PATIENT_ID_MAX_LENGTH}}$`);
const PARTNER_REF_PATTERN = /^[a-z0-9-]{1,32}$/;
const ONBOARDING_PATHS: readonly string[] = Object.values(ONBOARDING_ROUTES);

export interface IPatientHandoff {
  patientId: string;
  reference: string | null;
}

/**
 * @description Validates a raw `pid` value. Unusable values are treated as
 * absent, so the user falls back to an ordinary empty form rather than seeing
 * an error.
 * @param {string | null} raw - The raw query-parameter value.
 * @returns {string | null} The sanitized patient ID, or null when unusable.
 */
function sanitizePatientId(raw: string | null): string | null {
  const patientId = raw?.trim() ?? '';

  return PATIENT_ID_PATTERN.test(patientId) ? patientId : null;
}

/**
 * @description Validates a raw `ref` value. An unusable reference downgrades the
 * handoff to a plain pre-fill rather than failing it — the patient ID is still
 * worth having.
 * @param {string | null} raw - The raw query-parameter value.
 * @returns {string | null} The sanitized reference, or null when unusable.
 */
function sanitizeReference(raw: string | null): string | null {
  const reference = raw?.trim().toLowerCase() ?? '';

  return PARTNER_REF_PATTERN.test(reference) ? reference : null;
}

/**
 * @description Whether a landing path belongs to the onboarding area.
 * @param {string} pathname - The URL path, e.g. `/verify`.
 * @returns {boolean} True for an onboarding page.
 */
function isOnboardingPath(pathname: string): boolean {
  return ONBOARDING_PATHS.includes(pathname.split('/')[1] ?? '');
}

@Injectable({
  providedIn: 'root',
})
export class PatientHandoffService {
  /**
   * @description Captures a handoff carried by the landing URL and works out
   * where the browser should sit afterwards. Called once from an
   * `APP_INITIALIZER`, before the router's first navigation.
   * @param {string} href - The landing URL, typically `window.location.href`.
   * @param {number} now - Current epoch milliseconds, stamped for the TTL.
   * @returns {string | null} The URL to put in the address bar, or null when
   * the URL carried no handoff and should be left untouched.
   */
  captureFromUrl(href: string, now: number): string | null {
    let landing: URL;

    try {
      landing = new URL(href);
    } catch {
      return null;
    }
    if (!landing.searchParams.has(PATIENT_ID_PARAM)) {
      if (!landing.searchParams.has(PARTNER_REF_PARAM)) {
        return null;
      }

      landing.searchParams.delete(PARTNER_REF_PARAM);
      return landing.toString();
    }

    const patientId = sanitizePatientId(landing.searchParams.get(PATIENT_ID_PARAM));
    const reference = sanitizeReference(landing.searchParams.get(PARTNER_REF_PARAM));

    landing.searchParams.delete(PATIENT_ID_PARAM);
    landing.searchParams.delete(PARTNER_REF_PARAM);
    this.clear();

    if (patientId === null) {
      return landing.toString();
    }

    localStorage.setItem(PATIENT_ID_KEY, patientId);
    localStorage.setItem(CAPTURED_AT_KEY, String(now));

    if (reference !== null) {
      localStorage.setItem(REFERENCE_KEY, reference);
    }

    if (!isOnboardingPath(landing.pathname)) {
      landing.pathname = `/${APP_ROUTES.NEW_TEST}`;
    }

    return landing.toString();
  }

  /**
   * @description Returns the pending handoff without consuming it. Entries past
   * the TTL, stamped in the future, or with a missing or unreadable timestamp
   * are dropped rather than returned.
   * @returns {IPatientHandoff | null} The handoff, or null when none is usable.
   */
  peek(): IPatientHandoff | null {
    const patientId = localStorage.getItem(PATIENT_ID_KEY);
    const capturedAt = Number(localStorage.getItem(CAPTURED_AT_KEY));
    const age = Date.now() - capturedAt;

    if (!patientId || !capturedAt || age < 0 || age >= PATIENT_HANDOFF_TTL_MS) {
      this.clear();

      return null;
    }

    return { patientId, reference: localStorage.getItem(REFERENCE_KEY) };
  }

  /**
   * @description Returns the pending handoff and clears it.
   * @returns {IPatientHandoff | null} The handoff, or null when none is pending.
   */
  consume(): IPatientHandoff | null {
    const handoff = this.peek();
    this.clear();

    return handoff;
  }

  /**
   * @description Where to send a user who has just authenticated. A pending
   * handoff goes straight to the new test so the captured patient ID can
   * pre-fill it; everyone else lands on home.
   *
   * This lives in one place on purpose. The decision belongs to every
   * post-authentication landing site — the guard, password login, magic-link
   * verification and registration — and duplicating it is how the registration
   * path was missed on the first pass.
   * @returns {string} The route to navigate to, without a leading slash.
   */
  postAuthRoute(): string {
    return this.peek() ? APP_ROUTES.NEW_TEST : APP_ROUTES.HOME;
  }

  /**
   * @description Drops any pending handoff. Called on logout, and whenever a
   * landing URL supersedes an earlier handoff.
   * @returns {void}
   */
  clear(): void {
    localStorage.removeItem(PATIENT_ID_KEY);
    localStorage.removeItem(REFERENCE_KEY);
    localStorage.removeItem(CAPTURED_AT_KEY);
  }
}
